import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { configureStore, createSlice } from "@reduxjs/toolkit";
import { Provider, useDispatch, useSelector } from "react-redux";
import {
  DEMO_ADMIN_SESSIONS,
  DEMO_ATTENTION,
  DEMO_CLIENTS,
  DEMO_ENTITLEMENTS,
  DEMO_KPI,
  DEMO_PROFILE,
  DEMO_TENANT_DEFAULTS,
} from "./demoFixtures";

const DemoPortalContext = createContext(null);

function createDemoReduxStore(tenant, profileName) {
  const authSlice = createSlice({
    name: "auth",
    initialState: {
      isAuth: true,
      role: "owner",
      profile: { ...DEMO_PROFILE, full_name: profileName },
      tenant: { ...tenant },
      club: {
        id: "demo-club",
        name: tenant.name,
        team_members_count: 2,
        is_solo_club: false,
      },
      billing: { is_paid: true, is_trial: false, status: "active" },
      entitlements: DEMO_ENTITLEMENTS,
    },
    reducers: {
      demoPatchTenant(state, action) {
        state.tenant = { ...state.tenant, ...action.payload };
        if (action.payload.name) state.club.name = action.payload.name;
      },
      demoPatchProfile(state, action) {
        state.profile = { ...state.profile, ...action.payload };
      },
    },
  });

  return {
    store: configureStore({
      reducer: { auth: authSlice.reducer },
      middleware: (getDefault) => getDefault({ serializableCheck: false }),
    }),
    actions: authSlice.actions,
  };
}

function DemoPortalInner({ children, actions }) {
  const dispatch = useDispatch();
  const tenant = useSelector((s) => s.auth.tenant);
  const profile = useSelector((s) => s.auth.profile);
  const [journalDone, setJournalDone] = useState({});
  const [flash, setFlash] = useState("");
  const [search, setSearch] = useState("");

  const showFlash = useCallback((msg) => {
    setFlash(msg);
    window.setTimeout(() => setFlash(""), 1600);
  }, []);

  const updateDesign = useCallback(
    (patch) => {
      dispatch(actions.demoPatchTenant(patch));
      showFlash("Только у вас до перезагрузки");
    },
    [actions, dispatch, showFlash],
  );

  const updateProfileName = useCallback(
    (full_name) => {
      dispatch(actions.demoPatchProfile({ full_name }));
      showFlash("Только у вас до перезагрузки");
    },
    [actions, dispatch, showFlash],
  );

  const markJournalDone = useCallback(
    (sessionId) => {
      setJournalDone((prev) => ({ ...prev, [sessionId]: true }));
      showFlash("Отметка только в демо");
    },
    [showFlash],
  );

  const resetDemo = useCallback(() => {
    dispatch(actions.demoPatchTenant({ ...DEMO_TENANT_DEFAULTS }));
    dispatch(actions.demoPatchProfile({ full_name: DEMO_PROFILE.full_name }));
    setJournalDone({});
    setSearch("");
    showFlash("Демо сброшено");
  }, [actions, dispatch, showFlash]);

  const sessions = useMemo(
    () =>
      DEMO_ADMIN_SESSIONS.map((sess) => ({
        ...sess,
        unfinished: journalDone[sess.id] ? false : sess.unfinished,
        present_count: journalDone[sess.id]
          ? sess.expected_count
          : sess.present_count,
      })),
    [journalDone],
  );

  const attention = useMemo(() => {
    const unfinished = sessions.some((s) => s.unfinished);
    return DEMO_ATTENTION.filter((a) =>
      a.id === "att1" ? unfinished : true,
    );
  }, [sessions]);

  const clients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return DEMO_CLIENTS;
    return DEMO_CLIENTS.filter(
      (c) =>
        c.full_name.toLowerCase().includes(q) ||
        c.group.toLowerCase().includes(q),
    );
  }, [search]);

  const value = useMemo(
    () => ({
      isDemo: true,
      tenant,
      profile,
      sessions,
      attention,
      clients,
      kpi: DEMO_KPI,
      search,
      setSearch,
      flash,
      updateDesign,
      updateProfileName,
      markJournalDone,
      resetDemo,
    }),
    [
      tenant,
      profile,
      sessions,
      attention,
      clients,
      search,
      flash,
      updateDesign,
      updateProfileName,
      markJournalDone,
      resetDemo,
    ],
  );

  return (
    <DemoPortalContext.Provider value={value}>{children}</DemoPortalContext.Provider>
  );
}

export function DemoPortalProvider({ children }) {
  const boot = useRef(null);

  if (!boot.current) {
    boot.current = createDemoReduxStore(
      { ...DEMO_TENANT_DEFAULTS },
      DEMO_PROFILE.full_name,
    );
  }

  const { store, actions } = boot.current;

  return (
    <Provider store={store}>
      <DemoPortalInner actions={actions}>{children}</DemoPortalInner>
    </Provider>
  );
}

export function useDemoPortal() {
  const ctx = useContext(DemoPortalContext);
  if (!ctx) throw new Error("useDemoPortal outside DemoPortalProvider");
  return ctx;
}

export function useDemoPortalOptional() {
  return useContext(DemoPortalContext);
}
